/* eslint-disable max-lines-per-function */
import { noop } from 'lodash';
import { useMemo } from 'react';

import {
   ServiceProvidingLocation,
   ServiceProvidingLocationForCurrentPracticeQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { commonTranslationKeys, Ordering, phoneNumberFormatter, PromiseVoidFunction } from '@dreryk/react-common';
import { createDynamicColumnsWidth, Padding, TableColumnConfiguration } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { serviceProvidingLocationTranslationKeys } from '../../../../translations';
import { HealthcarePracticeFormBean } from '../../common/healthcarePractice';
import { ServiceProvidingLocationData } from '../../common/ServiceProvidingLocationData';

import { ServiceProvidingLocationFromServerUpdateCell } from './cells/ServiceProvidingLocationFromServerUpdateCell';
import { ServiceProvidingLocationRemoveCell } from './cells/ServiceProvidingLocationRemoveCell';

type UseServiceProvidingLocationsColumnsOptions = {
   healthcarePractice: HealthcarePracticeFormBean;
   refetchServiceProvidingLocations: PromiseVoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const useServiceProvidingLocationsColumns = (
   options: UseServiceProvidingLocationsColumnsOptions
): TableColumnConfiguration<ServiceProvidingLocation>[] => {
   const { healthcarePractice, refetchServiceProvidingLocations } = options;

   return useMemo((): TableColumnConfiguration<ServiceProvidingLocation>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         locationCode: '160px',
         nameAndAddress: '50%',
         phone: '190px',
         email: '50%',
         updateAction: '24px',
         removeAction: '24px',
      });

      return [
         {
            label: serviceProvidingLocationTranslationKeys.locationCode,
            width: columnsWidth.locationCode,
            accessor: (bean) => bean.locationCode,
            name: 'locationCode',
         },
         {
            label: serviceProvidingLocationTranslationKeys.nameAndAddress,
            width: columnsWidth.nameAndAddress,
            renderer: ({ bean }) => <ServiceProvidingLocationData name={bean.name} address={bean.address} />,
            accessor: noop,
            name: 'name',
            sortingDisabled: true,
         },
         {
            label: commonTranslationKeys.phone,
            width: columnsWidth.phone,
            accessor: (bean) => bean.phoneNumber,
            name: 'phone',
            sortingDisabled: true,
            formatter: phoneNumberFormatter,
         },
         {
            label: commonTranslationKeys.email,
            width: columnsWidth.email,
            accessor: (bean) => bean.emailAddress?.asString(),
            name: 'email',
            sortingDisabled: true,
         },
         {
            name: 'updateAction',
            width: columnsWidth.updateAction,
            accessor: noop,
            renderer: ({ bean }) => (
               <ServiceProvidingLocationFromServerUpdateCell
                  healthcarePractice={healthcarePractice}
                  id={bean.id}
                  refetchServiceProvidingLocations={refetchServiceProvidingLocations}
               />
            ),
            sortingDisabled: true,
            padding: Padding.NONE,
         },
         {
            name: 'removeAction',
            width: columnsWidth.removeAction,
            accessor: noop,
            renderer: ({ bean, rowsCount }) => (
               <ServiceProvidingLocationRemoveCell
                  serviceProvidingLocationId={bean.id}
                  serviceProvidingLocationName={bean.name}
                  locationCode={bean.locationCode}
                  onRemoved={refetchServiceProvidingLocations}
                  disabled={rowsCount <= 1}
               />
            ),
            sortingDisabled: true,
            padding: Padding.NONE,
         },
      ];
   }, [healthcarePractice, refetchServiceProvidingLocations]);
};

const DEFAULT_ORDERINGS: Ordering<ServiceProvidingLocation>[] = [
   {
      descending: false,
      property: 'locationCode',
   },
];

export const useServiceProvidingLocationsDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<ServiceProvidingLocation> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<ServiceProvidingLocation>(() => {
      const query = new ServiceProvidingLocationForCurrentPracticeQuery();
      query.removed = Slot.of(false);
      return query;
   }, DEFAULT_ORDERINGS);
};
